/* eslint-disable react/forbid-prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import HFIcon from '../icons';
import Modal from '../../../../components/Modal/Modal';
import { DangerButton, GhostButton } from '../../../../styles/ui/Button';
import { deriveTimeline, readableInk, skipReasonLabel } from '../derivations';
import {
   recordItemEvent,
   removeItemEvent,
   listItemEvents,
   updateItemCargoClass,
   updateItemCarOnly,
} from '../../../../services/routing/routingScheduleApi';
import EventConfirmDialog from './EventConfirmDialog';
import EventPickerModal from './EventPickerModal';
import ServiceClassificationModal from './ServiceClassificationModal';
import LocationPickerModal from './LocationPickerModal';
import RescheduleConfirmModal from './RescheduleConfirmModal';
import CancelItemDialog from '../../RoutingItemShow/components/CancelItemDialog';

const SLA_LABEL = { high: 'ALTA', med: 'MÉDIA', low: 'BAIXA' };
const STATUS_PILL = {
   done: { cls: 'ok', label: 'Concluído' },
   run: { cls: 'run', label: 'Em andamento' },
   idle: { cls: 'idle', label: 'Pendente' },
};
const TERMINAL_STATUSES = ['REALIZADO', 'NAO_REALIZADO'];

// Fallback mock: deriva timeline de presence/otw quando o stop não tem raw
// (modo ?mock=1). Com dados reais a timeline vem de raw.active_events.
function buildMockEvents(stop) {
   const events = (stop.presence || []).map((p) => ({
      kind: p.kind,
      t: p.t,
      label: p.label,
   }));
   if (stop.otw) {
      events.push({ kind: 'otw', t: stop.otw.t, label: stop.otw.label });
   }
   return events;
}

function formatBRL(value) {
   if (value == null) return null;
   return Number(value).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

export default function StopDrawer({
   stop = null,
   tech = null,
   subcluster = null,
   date = null,
   period = null,
   base = null,
   canRecord = false,
   canManage = false,
   canRemoveEvent = false,
   canUpdateItem = false,
   canCancelItem = false,
   catalog = [],
   isMock = false,
   onClose,
   onEventRecorded = null,
   onEventRemoved = null,
   onItemUpdated = null,
   onItemCancelled = null,
}) {
   // Classificação de serviço (handoff 0037 §A) — modal de quantidades + tempo.
   const [isClassifyOpen, setIsClassifyOpen] = useState(false);
   // Ajuste manual de geolocalização (handoff 0043) — modal com pino no mapa.
   const [isLocationOpen, setIsLocationOpen] = useState(false);
   // Exclusão (soft delete → CANCELADO) — dialog de confirmação reaproveitado
   // do RoutingItemShow. Só para itens PENDENTE no dock (não roteirizados) —
   // regra que o handoff 0059 tornou universal no backend (item_already_routed).
   const [isCancelOpen, setIsCancelOpen] = useState(false);
   // Fluxo de registro em duas etapas: picker (escolhe o kind com regras de
   // habilitação) → dialog de confirmação.
   const [isPickerOpen, setIsPickerOpen] = useState(false);
   const [pendingKind, setPendingKind] = useState(null);

   // Reagendamento (interino, sem endpoint /reschedule): confirma e abre a tela
   // de criação de novo item avulso em outra guia. Disparado pelo botão em item
   // NAO_REALIZADO e automaticamente após marcar "Não realizado".
   const [isRescheduleConfirmOpen, setIsRescheduleConfirmOpen] = useState(false);

   // Remoção de evento (M2 §5): confirm antes de chamar o backend (a remoção de
   // um terminal cascateia o sintético e reabre/destrava o item).
   const [pendingRemove, setPendingRemove] = useState(null);
   const [isRemoving, setIsRemoving] = useState(false);

   // Edição de carga/restrição (handoff 0032). savingField trava o controle em
   // voo ('cargo_class' | 'car_only' | null) — evita cliques duplos no toggle.
   const [savingField, setSavingField] = useState(null);

   // Timeline detalhada (GET .../items/:itemId/events) — traz as notas que o
   // snapshot resumido não carrega. Enquanto não chega (ou se falhar), usa o
   // active_events resumido do snapshot.
   const [detailedEvents, setDetailedEvents] = useState(null);

   const itemId = stop?.itemId;
   const hasRaw = Boolean(stop?.raw);
   useEffect(() => {
      setDetailedEvents(null);
      if (!itemId || !hasRaw || isMock) return undefined;
      let cancelled = false;
      listItemEvents(date, period, itemId)
         .then((events) => {
            if (!cancelled && Array.isArray(events)) {
               setDetailedEvents(events);
            }
         })
         .catch(() => {
            // Silencioso: o resumo do snapshot cobre a timeline sem notas.
         });
      return () => {
         cancelled = true;
      };
   }, [itemId, hasRaw, isMock, date, period]);

   // Re-busca a timeline após uma mutação (registro/remoção). Pós-registro de
   // terminal "precoce" (M2 §4) é onde isso importa: o sintético NÃO vem na
   // resposta do POST, então só o refetch traz o par terminal+sintético.
   const refreshTimeline = useCallback(() => {
      if (!itemId || !hasRaw || isMock) return Promise.resolve();
      return listItemEvents(date, period, itemId)
         .then((events) => {
            if (Array.isArray(events)) setDetailedEvents(events);
         })
         .catch(() => {
            // Silencioso: o update otimista já refletiu o essencial.
         });
   }, [itemId, hasRaw, isMock, date, period]);

   const events = useMemo(() => {
      if (!stop) return [];
      if (!stop.raw) return buildMockEvents(stop);
      return deriveTimeline(detailedEvents ?? stop.raw.active_events, date);
   }, [stop, detailedEvents, date]);

   if (!stop) return null;

   const statusPill = STATUS_PILL[stop.status] || STATUS_PILL.idle;
   const raw = stop.raw || null;
   const isTerminal = raw && TERMINAL_STATUSES.includes(raw.item_status);

   // Gates (spec §9.6/§10.11): registrar evento exige record_event; item em
   // estado terminal só aceita sobrescrita de quem tem manage — pra quem não
   // tem, o controle é ESCONDIDO (não desabilitado).
   const showEventSection = !isMock && raw && canRecord;
   const showEventButtons = showEventSection && (!isTerminal || canManage);

   // Remover evento (M2 §5): permissão própria, independente de canRecord. Só o
   // ÚLTIMO evento ativo é removível (LIFO); sintéticos nunca são o último
   // (nascem antes do terminal) e não expõem remoção.
   const showRemove = !isMock && Boolean(raw) && canRemoveEvent;
   const lastEventIndex = events.length - 1;

   const orderValue = formatBRL(raw?.order_value);

   // ─── Restrição de horário marcado (handoff 0049) ─────────────────────────
   // skipReason: o otimizador recusou o item (ficou no dock) — AVISO, não trava;
   // pode ser arrastado manualmente. breached: item roteirizado cuja ETA passou
   // do fim da janela (via reorder). Os dois nunca coexistem (skip só no dock).
   const skipReason = !isMock ? skipReasonLabel(stop.optimizationSkipReason) : null;
   const breached = Boolean(stop.timeWindowBreach);

   // ─── Seleção de serviços (handoff 0039 §3) ───────────────────────────────
   // services[] do catálogo unificado + total de unidades. services vazio = não
   // classificado → o item não pode entrar na otimização (barrado no disparo).
   // itemStatus é computado logo abaixo (carga); aqui só leio do stop.
   const itemStatusRaw = raw?.item_status ?? null;
   const services = stop.services || [];
   const isClassified = services.length >= 1;
   const serviceBreakdown = services.map((s) => ({
      id: s.commission_table_id,
      label: s.service_description,
      qty: s.quantity,
   }));
   const showClassify = !isMock && Boolean(raw);
   const canEditClassification =
      showClassify && canUpdateItem && itemStatusRaw !== 'CANCELADO';
   // Aviso bloqueante só faz sentido para item ainda PENDENTE (é o que a
   // otimização barra). Terminal/cancelado já não otimiza.
   const showUnclassifiedWarning =
      showClassify && !isClassified && itemStatusRaw === 'PENDENTE';

   // ─── Tint do cabeçalho (intuitividade — fix) ─────────────────────────────
   // Item em rota: cabeçalho na cor da rota (posiciona ao lado do painel de
   // rotas, à direita). Item não-roteirizado: cabeçalho #373737/branco (ao lado
   // do bloco "Não roteirizados", à esquerda).
   const isUnrouted = !subcluster;
   const routeColor = subcluster?.color || null;
   let headTint = null;
   if (isUnrouted) headTint = { bg: '#373737', ink: '#ffffff' };
   else if (routeColor) headTint = { bg: routeColor, ink: readableInk(routeColor) };

   // ─── Carga e restrições (handoff 0032) ───────────────────────────────────
   // Valores atuais (adapter expõe; raw é fallback). cargoClass e carOnly são
   // eixos INDEPENDENTES — não derivar um do outro nem do `vol`.
   const itemStatus = raw?.item_status ?? null;
   const cargoClass = stop.cargoClass ?? raw?.cargo_class ?? null;
   const carOnly = stop.carOnly ?? Boolean(raw?.car_only);
   // Trava de edição agora é POR-ITEM (handoff 0046 §3): bloqueia quando o item
   // está dentro de uma rota (sinal `is_routed` do snapshot; fallback = ausência
   // de subcluster). Item no dock segue editável mesmo com o schedule otimizado.
   const isRouted = raw?.is_routed ?? !isUnrouted;

   // Mostrar a seção só com dados reais (mock não tem item de backend).
   const showCargoSection = !isMock && Boolean(raw);
   // cargo_class é só etiqueta: editável exceto item CANCELADO (§3).
   const canEditCargoClass =
      showCargoSection && canUpdateItem && itemStatus !== 'CANCELADO';
   // car_only entra no VRP: editável só com PENDENTE e item FORA de rota (§3).
   // Fora disso, exibe leitura + motivo (nunca silencioso).
   const canEditCarOnly =
      showCargoSection &&
      canUpdateItem &&
      itemStatus === 'PENDENTE' &&
      !isRouted;
   // Motivo do bloqueio do toggle quando o operador TEM permissão mas o estado
   // impede — orienta em vez de só desabilitar mudo.
   let carOnlyLockHint = null;
   if (showCargoSection && canUpdateItem && !canEditCarOnly) {
      if (isRouted) {
         carOnlyLockHint =
            'Item já está em uma rota — remova-o da rota para editar a restrição.';
      } else if (itemStatus !== 'PENDENTE') {
         carOnlyLockHint = 'Disponível apenas para itens pendentes.';
      }
   }

   // ─── Geolocalização (handoff 0043) ───────────────────────────────────────
   // needsGeocoding: item PENDENTE sem coordenada → barrado na otimização. A
   // seção mostra o ponto atual (ou o aviso "sem localização") e o botão de
   // ajuste no mapa. O endpoint só aceita item PENDENTE e FORA de rota (§3) —
   // mesmas guardas do car_only, motivo nunca mudo.
   const lat = stop.lat ?? (raw?.address_latitude != null ? Number(raw.address_latitude) : null);
   const lng = stop.lng ?? (raw?.address_longitude != null ? Number(raw.address_longitude) : null);
   const hasCoord = lat != null && lng != null;
   const needsGeocoding = Boolean(stop.needsGeocoding);
   const showLocationSection = !isMock && Boolean(raw);
   const canEditLocation =
      showLocationSection &&
      canUpdateItem &&
      itemStatus === 'PENDENTE' &&
      !isRouted;
   let locationLockHint = null;
   if (showLocationSection && canUpdateItem && !canEditLocation) {
      if (isRouted) {
         locationLockHint =
            'Item já está em uma rota — remova-o da rota para ajustar a localização.';
      } else if (itemStatus !== 'PENDENTE') {
         locationLockHint = 'Disponível apenas para itens pendentes.';
      }
   }

   // ─── Excluir item (soft delete → CANCELADO) ──────────────────────────────
   // Reaproveita o cancelScheduleItem (DELETE /routing/items/:id). Backend exige
   // PENDENTE e, desde o handoff 0059, item FORA de rota pra QUALQUER perfil
   // (422 item_already_routed) — o gate do dock abaixo já espelha isso. A
   // reversão existe: ação "Restaurar" no card "Itens excluídos" e na ficha
   // (exclusiva de routing:schedule_item:cancel).
   const canDelete =
      !isMock &&
      Boolean(raw) &&
      canCancelItem &&
      itemStatus === 'PENDENTE' &&
      !isRouted;

   const handleCargoClassChange = async (value) => {
      const next = value === '' ? null : value;
      setSavingField('cargo_class');
      try {
         const updated = await updateItemCargoClass(stop.itemId, next);
         toast.success('Classe de carga atualizada.');
         onItemUpdated?.({ itemId: stop.itemId, scheduleItem: updated });
      } catch (err) {
         toast.error(
            err?.response?.data?.message ??
               'Não foi possível atualizar a classe de carga.'
         );
      } finally {
         setSavingField(null);
      }
   };

   const handleCarOnlyToggle = async () => {
      const next = !carOnly;
      setSavingField('car_only');
      try {
         const updated = await updateItemCarOnly(stop.itemId, next);
         toast.success('Restrição de veículo atualizada.');
         onItemUpdated?.({ itemId: stop.itemId, scheduleItem: updated });
      } catch (err) {
         const code = err?.response?.data?.errors?.[0]?.code;
         const apiMessage = err?.response?.data?.message;
         if (code === 'item_already_routed') {
            toast.error(
               apiMessage ??
                  'Item já está em uma rota — remova-o da rota para editar a restrição.'
            );
         } else if (code === 'invalid_state_for_car_only') {
            toast.error(
               apiMessage ??
                  'Só itens pendentes aceitam a restrição "só carro".'
            );
         } else {
            toast.error(
               apiMessage ?? 'Não foi possível atualizar a restrição de veículo.'
            );
         }
      } finally {
         setSavingField(null);
      }
   };

   const handleSubmitEvent = async (payload) => {
      const result = await recordItemEvent(date, period, stop.itemId, payload);
      toast.success(result.message);
      setPendingKind(null);
      // item.item_status do response = estado pós-espelho → update otimista do
      // board (status + locked) sem refetch do snapshot.
      onEventRecorded?.({
         itemId: stop.itemId,
         event: result.event,
         item: result.item,
      });
      // Refetch da timeline: terminal "precoce" gera um sintético que NÃO vem na
      // resposta do POST (M2 §4) — só o GET reflete o par terminal+sintético.
      await refreshTimeline();
      // Após marcar "Não realizado" (evento CANCELLED → item NAO_REALIZADO),
      // oferece reagendar criando um novo item avulso (nova guia).
      if (
         payload?.event_kind === 'CANCELLED' &&
         result?.item?.item_status === 'NAO_REALIZADO'
      ) {
         setIsRescheduleConfirmOpen(true);
      }
   };

   // Abre a criação de novo item avulso em OUTRA GUIA, já com o order_number
   // preenchido (Parte D lê ?tab=MANUAL e ?order_number). O rastreio da cadeia
   // passa a ser por order_number — sem endpoint /reschedule.
   const handleConfirmReschedule = () => {
      const params = new URLSearchParams({ tab: 'MANUAL' });
      if (raw?.order_number) params.set('order_number', raw.order_number);
      window.open(
         `/routing/items/new?${params.toString()}`,
         '_blank',
         'noopener'
      );
      setIsRescheduleConfirmOpen(false);
   };

   const handleRemoveEvent = async () => {
      if (!pendingRemove?.eventId) return;
      setIsRemoving(true);
      try {
         const result = await removeItemEvent(
            date,
            period,
            pendingRemove.eventId
         );
         toast.success(result.message);
         setPendingRemove(null);
         // data.item traz o status pós-remoção; data.removed[] tem 1 ou 2
         // entradas (2 quando cascateou o sintético). O board destrava/reabre
         // por esse retorno.
         onEventRemoved?.({
            itemId: stop.itemId,
            removed: result.removed,
            item: result.item,
         });
         await refreshTimeline();
      } catch (err) {
         const code = err?.response?.data?.errors?.[0]?.code;
         const apiMessage = err?.response?.data?.message;
         if (err?.response?.status === 403 || code === 'remove_event_forbidden') {
            toast.error(
               apiMessage ?? 'Você não tem permissão para remover eventos.'
            );
         } else if (code === 'event_not_last') {
            toast.error(
               apiMessage ??
                  'Só o último evento da timeline pode ser removido.'
            );
         } else {
            toast.error(apiMessage ?? 'Erro ao remover o evento.');
         }
         setPendingRemove(null);
      } finally {
         setIsRemoving(false);
      }
   };

   return (
      <div
         className={`hf-drawer ${isUnrouted ? 'unrouted' : 'routed'}`}
         role="dialog"
         aria-label={`Detalhe da parada ${stop.id}`}
      >
         <div
            className={`hf-drawer-head ${headTint ? 'tinted' : ''}`}
            style={
               headTint
                  ? { background: headTint.bg, color: headTint.ink }
                  : undefined
            }
         >
            {/* Nº do pedido como título; cliente (com ellipsis) de subtítulo
                (decisão 2026-07-03). */}
            <div className="grow">
               <div className="ttl">{stop.id}</div>
               <div className="sub" title={stop.nm}>
                  {stop.nm}
                  {stop.seq ? ` · Sequência ${stop.seq}` : ''}
               </div>
            </div>
            {/* REALIZADO da planilha legado (v0), sem timeline — spec §10.6 */}
            {stop.isLegacyDone && (
               <span className="hf-pill idle" title="Importado da operação v0">
                  Sem timeline
               </span>
            )}
            <span className={`hf-pill ${statusPill.cls}`}>
               <span className="dot" />
               {statusPill.label}
            </span>
            {showEventButtons && (
               <button
                  type="button"
                  className="hf-btn"
                  onClick={() => setIsPickerOpen(true)}
               >
                  {HFIcon.plus} Evento
               </button>
            )}
            <button
               type="button"
               className="close"
               onClick={onClose}
               title="Fechar (Esc)"
               aria-label="Fechar"
            >
               {HFIcon.x}
            </button>
         </div>

         <div className="hf-drawer-body scroll">
            <div className="hf-drawer-section">
               <div className="section-title">Pedido</div>
               <div className="row">
                  <span className="k">ETA</span>
                  <span className="v tabular">{stop.eta}</span>
               </div>
               {/* SLA só é confiável com ETA (pós-otimização). Sem ETA o
                   deriveSla cai no fallback 'low' e mostraria BAIXA falso —
                   por isso a row é gateada à presença de ETA. */}
               {stop.eta !== '—' && (
                  <div className="row">
                     <span className="k">SLA</span>
                     <span className={`hf-sla ${stop.sla}`}>
                        {SLA_LABEL[stop.sla]}
                     </span>
                  </div>
               )}
               {orderValue && (
                  <div className="row">
                     <span className="k">Valor</span>
                     <span className="v tabular">{orderValue}</span>
                  </div>
               )}
            </div>

            {skipReason && (
               <div className="hf-drawer-section">
                  <div className="section-title">Otimização</div>
                  <div className="hf-svc-warn">
                     {HFIcon.alert} {skipReason}.
                  </div>
                  <div className="row">
                     <span className="v hint">
                        Aviso, não bloqueio — você pode arrastar o item para uma
                        rota manualmente mesmo assim.
                     </span>
                  </div>
               </div>
            )}

            {showClassify && (
               <div className="hf-drawer-section">
                  <div className="section-title">Atribuição de serviços</div>
                  {isClassified ? (
                     <div className="hf-svc-list">
                        {serviceBreakdown.map((s) => (
                           <div key={s.id} className="hf-svc-chip">
                              {s.label}
                              <span className="qty">×{s.qty}</span>
                           </div>
                        ))}
                     </div>
                  ) : (
                     <div className="row">
                        <span className="v hint">Nenhum serviço atribuído.</span>
                     </div>
                  )}
                  {showUnclassifiedWarning && (
                     <div className="hf-svc-warn">
                        {HFIcon.alert} Nenhum serviço atribuído — necessário antes
                        de otimizar.
                     </div>
                  )}
                  {canEditClassification && (
                     <button
                        type="button"
                        className="hf-btn"
                        onClick={() => setIsClassifyOpen(true)}
                     >
                        {HFIcon.pencil}{' '}
                        {isClassified ? 'Editar serviços' : 'Atribuir serviços'}
                     </button>
                  )}
               </div>
            )}

            {showCargoSection && (
               <div className="hf-drawer-section">
                  <div className="section-title">Carga e restrições</div>
                  <div className="row">
                     <span className="k">Classe de carga</span>
                     {canEditCargoClass ? (
                        <select
                           className="hf-drawer-select"
                           value={cargoClass ?? ''}
                           disabled={savingField === 'cargo_class'}
                           onChange={(e) =>
                              handleCargoClassChange(e.target.value)
                           }
                           aria-label="Classe de carga"
                        >
                           <option value="">— sem classe</option>
                           <option value="A">A</option>
                           <option value="B">B</option>
                           <option value="C">C</option>
                           <option value="D">D</option>
                        </select>
                     ) : (
                        <span className="v">{cargoClass || '—'}</span>
                     )}
                  </div>
                  <div className="row">
                     <span className="k">
                        Só carro
                        <span
                           className="hf-help"
                           title="Restrição dura: este item só pode ir em carro. Entra no cálculo da otimização."
                        >
                           ?
                        </span>
                     </span>
                     {canEditCarOnly ? (
                        <button
                           type="button"
                           className={`hf-toggle ${carOnly ? 'on' : ''}`}
                           role="switch"
                           aria-checked={carOnly}
                           aria-label="Restrição só carro"
                           disabled={savingField === 'car_only'}
                           onClick={handleCarOnlyToggle}
                        >
                           <span className="knob" />
                        </button>
                     ) : (
                        <span className="v">{carOnly ? 'Sim' : 'Não'}</span>
                     )}
                  </div>
                  {carOnlyLockHint && (
                     <div className="row">
                        <span className="v hint">{carOnlyLockHint}</span>
                     </div>
                  )}
               </div>
            )}

            {showLocationSection && (
               <div className="hf-drawer-section">
                  <div className="section-title">Localização</div>
                  <div className="row">
                     <span className="k">Coordenada</span>
                     <span className="v tabular">
                        {hasCoord
                           ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
                           : 'Sem localização definida'}
                     </span>
                  </div>
                  {needsGeocoding && (
                     <div className="hf-svc-warn">
                        {HFIcon.alert} Item sem coordenada — defina a localização
                        antes de otimizar.
                     </div>
                  )}
                  {canEditLocation ? (
                     <button
                        type="button"
                        className="hf-btn"
                        onClick={() => setIsLocationOpen(true)}
                     >
                        {HFIcon.pin}{' '}
                        {hasCoord ? 'Ajustar localização' : 'Definir localização'}
                     </button>
                  ) : (
                     locationLockHint && (
                        <div className="row">
                           <span className="v hint">{locationLockHint}</span>
                        </div>
                     )
                  )}
               </div>
            )}

            <div className="hf-drawer-section">
               <div className="section-title">Cliente</div>
               <div className="row">
                  <span className="k">Nome</span>
                  <span className="v">{stop.nm}</span>
               </div>
               {raw?.customer_phone && (
                  <div className="row">
                     <span className="k">Telefone</span>
                     <span className="v tabular">{raw.customer_phone}</span>
                  </div>
               )}
               <div className="row">
                  <span className="k">Endereço</span>
                  <span className="v" style={{ textAlign: 'right' }}>
                     {stop.addr}
                  </span>
               </div>
            </div>

            <div className="hf-drawer-section">
               <div className="section-title">Janela de atendimento</div>
               <div className="row">
                  <span className="k">Janela</span>
                  <span className="v tabular">{stop.win || '—'}</span>
               </div>
               {breached && (
                  <div className="hf-svc-warn">
                     {HFIcon.alert} A chegada estimada (ETA) passou do fim da
                     janela. Reordene a rota para tentar encaixar no horário.
                  </div>
               )}
            </div>

            {subcluster && (
               <div className="hf-drawer-section">
                  <div className="section-title">Rota</div>
                  <div className="row">
                     <span className="k">Rota</span>
                     <span className="v">{subcluster.label}</span>
                  </div>
                  {subcluster.vehicle && (
                     <div className="row">
                        <span className="k">Veículo</span>
                        <span className="v">
                           {subcluster.vehicle.identifier}
                        </span>
                     </div>
                  )}
                  {tech ? (
                     <div className="hf-drawer-tech">
                        <div className={`hf-av lg ${tech.c}`}>{tech.init}</div>
                        <div className="info">
                           <div className="nm">{tech.name}</div>
                           {tech.department?.department_name && (
                              <div className="sub">
                                 {tech.department.department_name}
                              </div>
                           )}
                        </div>
                     </div>
                  ) : (
                     <div className="row">
                        <span className="v" style={{ color: '#8B8B82' }}>
                           Sem técnico atribuído.
                        </span>
                     </div>
                  )}
               </div>
            )}

            {events.length > 0 && (
               <div className="hf-drawer-section">
                  <div className="section-title">Histórico</div>
                  {/* Ordem cronológica: 1º evento no topo. Eventos registrados
                      fora do dia do agendamento mostram a data (DD/MM). */}
                  <div className="hf-drawer-timeline">
                     {events.map((ev, idx) => (
                        <div
                           // eslint-disable-next-line react/no-array-index-key
                           key={idx}
                           className={`ev ${ev.kind} ${
                              ev.isSynthetic ? 'synthetic' : ''
                           }`}
                        >
                           <span className="t">
                              {ev.d && <small>{ev.d}</small>}
                              {ev.t}
                           </span>
                           <span className="d" />
                           <span className="ev-main">
                              <span className="ev-label">
                                 {ev.label}
                                 {ev.isSynthetic && (
                                    <span
                                       className="ev-auto"
                                       title="Evento automático gerado pelo sistema"
                                    >
                                       auto
                                    </span>
                                 )}
                                 {ev.notes && (
                                    <span className="note">“{ev.notes}”</span>
                                 )}
                              </span>
                              {showRemove && !ev.isSynthetic && (
                                 <button
                                    type="button"
                                    className="ev-remove"
                                    disabled={idx !== lastEventIndex}
                                    title={
                                       idx === lastEventIndex
                                          ? 'Remover evento'
                                          : 'Remova primeiro o evento posterior'
                                    }
                                    aria-label="Remover evento"
                                    onClick={() => setPendingRemove(ev)}
                                 >
                                    {HFIcon.trash}
                                 </button>
                              )}
                           </span>
                        </div>
                     ))}
                  </div>
               </div>
            )}

            {stop.isLegacyDone && (
               <div className="hf-drawer-section">
                  <div className="section-title">Histórico</div>
                  <div className="row">
                     <span className="v">
                        Concluído na operação v0 (planilha) — sem timeline de
                        eventos.
                     </span>
                  </div>
               </div>
            )}

            {showEventSection && isTerminal && !canManage && (
               <div className="hf-drawer-section">
                  <div className="row">
                     <span className="v">
                        Item em estado terminal — somente operador sênior pode
                        sobrescrever o desfecho.
                     </span>
                  </div>
               </div>
            )}

            {/* Reagendar item NAO_REALIZADO — cria um novo item avulso (mesmo
                order_number, nova data) em outra guia. Não há gate de permissão
                aqui: a tela de criação é o gate real (403 se faltar
                routing:schedule_item:create). */}
            {!isMock && raw && itemStatus === 'NAO_REALIZADO' && (
               <div className="hf-drawer-section">
                  <button
                     type="button"
                     className="hf-btn primary"
                     onClick={() => setIsRescheduleConfirmOpen(true)}
                  >
                     {HFIcon.refresh} Reagendar
                  </button>
                  <div className="row">
                     <span className="v">
                        Abre a criação de um novo item avulso em outra guia, com
                        o número do pedido preenchido.
                     </span>
                  </div>
               </div>
            )}

            {canDelete && (
               <div className="hf-drawer-section">
                  <button
                     type="button"
                     className="hf-btn danger"
                     onClick={() => setIsCancelOpen(true)}
                  >
                     {HFIcon.trash} Excluir item
                  </button>
               </div>
            )}
         </div>

         {isClassifyOpen && (
            <ServiceClassificationModal
               stop={stop}
               catalog={catalog}
               onClose={() => setIsClassifyOpen(false)}
               onSaved={(payload) => {
                  onItemUpdated?.(payload);
               }}
            />
         )}

         {isLocationOpen && (
            <LocationPickerModal
               itemId={stop.itemId}
               stop={stop}
               base={base}
               onClose={() => setIsLocationOpen(false)}
               onSaved={(payload) => {
                  onItemUpdated?.(payload);
               }}
            />
         )}

         <CancelItemDialog
            isOpen={isCancelOpen}
            onClose={() => setIsCancelOpen(false)}
            item={{ id: stop.itemId, order_number: raw?.order_number }}
            onCancelled={() => {
               setIsCancelOpen(false);
               onItemCancelled?.();
            }}
         />

         <RescheduleConfirmModal
            isOpen={isRescheduleConfirmOpen}
            onClose={() => setIsRescheduleConfirmOpen(false)}
            orderNumber={raw?.order_number ?? null}
            onConfirm={handleConfirmReschedule}
         />

         <EventPickerModal
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            stop={stop}
            onPick={(kindDef) => {
               setIsPickerOpen(false);
               setPendingKind(kindDef);
            }}
         />

         <EventConfirmDialog
            isOpen={Boolean(pendingKind)}
            onClose={() => setPendingKind(null)}
            stop={stop}
            kindDef={pendingKind}
            isTerminalOverride={Boolean(isTerminal)}
            onSubmit={handleSubmitEvent}
         />

         <Modal
            isOpen={Boolean(pendingRemove)}
            onClose={() => (isRemoving ? null : setPendingRemove(null))}
            title="Remover evento"
            maxWidth="440px"
            // ⚠️ Botões do kit, NÃO as classes `hf-btn` do board: o Modal é
            // portaled pro body, fora do escopo .hf — as CSS vars não existem
            // lá e o hf-btn fica transparente/invisível.
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setPendingRemove(null)}
                     disabled={isRemoving}
                  >
                     Cancelar
                  </GhostButton>
                  <DangerButton
                     type="button"
                     onClick={handleRemoveEvent}
                     disabled={isRemoving}
                  >
                     {isRemoving ? 'Removendo…' : 'Remover evento'}
                  </DangerButton>
               </>
            }
         >
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
               Remover o evento{' '}
               <strong>{pendingRemove?.label}</strong>
               {pendingRemove?.t ? ` (${pendingRemove.t})` : ''}? Esta ação
               reabre e <strong>destrava</strong> o item.
               {['EXECUTED', 'PARTIAL', 'CANCELLED'].includes(
                  pendingRemove?.eventKind
               ) && (
                  <>
                     {' '}
                     Se houver um evento automático vinculado a este desfecho,
                     ele será removido junto.
                  </>
               )}{' '}
               O evento sai da timeline ativa, mas permanece na auditoria.
            </div>
         </Modal>
      </div>
   );
}

StopDrawer.propTypes = {
   stop: PropTypes.object,
   tech: PropTypes.object,
   subcluster: PropTypes.object,
   date: PropTypes.string,
   period: PropTypes.string,
   base: PropTypes.object,
   canRecord: PropTypes.bool,
   canManage: PropTypes.bool,
   canRemoveEvent: PropTypes.bool,
   canUpdateItem: PropTypes.bool,
   canCancelItem: PropTypes.bool,
   catalog: PropTypes.array,
   isMock: PropTypes.bool,
   onClose: PropTypes.func.isRequired,
   onEventRecorded: PropTypes.func,
   onEventRemoved: PropTypes.func,
   onItemUpdated: PropTypes.func,
   onItemCancelled: PropTypes.func,
};
